import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from 'state/axios';
import toast from 'react-hot-toast';
import { Formik } from 'formik';
import * as yup from 'yup';
import { 
    Box, 
    Typography,   
    CircularProgress, 
    TextField, 
    Button, 
    Checkbox, 
    FormControlLabel, 
    MenuItem, 
    Select, 
    FormControl, 
    InputLabel,
} from '@mui/material';
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { de, enGB } from 'date-fns/locale';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import { parseUTCDate } from 'state/helpers';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';
import CustomDatePicker from "components/CustomDatePicker";


const HolidayPage = () => {
    useAuthCheck();
    const { holidayId } = useParams();    
    const navigate = useNavigate();
    const locales = { 'en-us': undefined, 'en-gb': enGB, de };

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const checkoutSchema = yup.object().shape({
        holidayName: yup.string().required('Required'),
    });

    const [formValues, setFormValues] = useState({
        holidayName: "",
        frequency: "once",        
        startDate: new Date(),
        endDate: new Date(),
        description: "",       
        isLunar: false,
        isActive: true,
    });

    
    useEffect(() => {     
        const fetchHoliday = async () => {
            try {
                setError(null);
                setLoading(true);

                if (holidayId) {
                    const url = `/holidays/${holidayId}`;
                    const res = await api.get(url);
                    setFormValues({
                        holidayName: res.data.holidayName || "",
                        frequency: res.data.frequency || "once",        
                        startDate: res.data.startDate ? new Date(res.data.startDate) : null,
                        endDate: res.data.endDate ? new Date(res.data.endDate) : null,
                        description: res.data.description || "",  
                        isLunar: res.data.isActive ?? true,
                        isActive: res.data.isActive ?? true,
                    });
                }
            } catch (error) {
                setError(error);
            } finally {
                setLoading(false);
            }
        }; 
        
        fetchHoliday();
    }, [holidayId]); 

    const handleFormSubmit = async (values, onSubmitProps) => {
        try {
            const payload = {
                ...values,
                startDate: values.startDate
                    ? parseUTCDate(values.startDate) 
                    : "",
                endDate: values.endDate
                    ? parseUTCDate(values.endDate) 
                    : "",
            };            
            const url = holidayId 
                ? `/holidays/update/${holidayId}` 
                : '/holidays/new';      
            const method = holidayId ? 'put' : 'post';
            
            const response = await api({
                method,
                url,
                data: payload,
            });

            if (![200, 201].includes(response.status)) {
                toast.error("Failed to submit the form!");
                return;
            }

            toast.success(
                holidayId
                    ? 'Holiday updated successfully.' 
                    : 'Holiday created successfully.'
            );        
            
            navigate("/holidays");
        } catch (error) {
            setError(error);
            toast.error(error.message || "Failed to submit the form!");
        }
    };


    return (
        <Box className="main-container-box">                
            <FlexBetween>
                <Header
                    title="Holiday"
                    subtitle={`${loading ? 'Loading...' : 
                        holidayId ? 'Update' : 'Create'}`}
                />
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <StyledButton 
                            onClick={() => navigate("/holidays")}
                        >
                            <ArrowBackIosNewIcon />
                        </StyledButton>
                    </Box>
                )}
            </FlexBetween>
            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <Typography color="error">Error: {error?.message || ''}</Typography>
                </FlexCenter>
            ) : (
                <Box height={LAYOUT_HEIGHT} sx={{ overflowY: 'auto' }}>
                    <LocalizationProvider 
                        dateAdapter={AdapterDateFns}
                        adapterLocale={locales['en-gb']}
                    >
                        <Formik
                            initialValues={formValues}
                            enableReinitialize={true}
                            validationSchema={checkoutSchema}
                            onSubmit={handleFormSubmit}
                        >
                            {({
                                values,
                                errors,
                                touched,
                                handleBlur,
                                handleChange,
                                handleSubmit,
                                setFieldValue,
                            }) => (
                                <form onSubmit={handleSubmit}>
                                    <Box
                                        display="grid"
                                        gap={2}
                                        gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                                    >
                                        <TextField
                                            fullWidth
                                            required
                                            variant="filled"
                                            type="text"
                                            label="Holiday Name"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.holidayName}
                                            name="holidayName"
                                            error={!!touched.holidayName && !!errors.holidayName}
                                            helperText={touched.holidayName && errors.holidayName}
                                            sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                                        />
                                        <FormControl 
                                            required 
                                            variant="filled"
                                            sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}
                                        >
                                            <InputLabel>Frequency</InputLabel>
                                            <Select
                                                value={values.frequency || ''}
                                                onChange={handleChange}
                                                label="Frequency"
                                                name="frequency"
                                                variant="filled"                                      
                                            >
                                                {['once', 'weekly', 'monthly', 'yearly'].map(item => (
                                                <MenuItem key={item} value={item}>
                                                    {item.charAt(0).toUpperCase() + item.slice(1)}
                                                </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        
                                        <CustomDatePicker
                                            label="Start Date"
                                            name="startDate"
                                            value={values.startDate}
                                            maxDate={values.endDate}
                                            onChange={setFieldValue}
                                            touched={touched}
                                            errors={errors}
                                            sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}
                                        />

                                        <CustomDatePicker
                                            label="End Date"
                                            name="endDate"
                                            value={values.endDate}
                                            minDate={values.startDate}
                                            onChange={setFieldValue}
                                            touched={touched}
                                            errors={errors}
                                            sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}
                                        />

                                        <TextField
                                            fullWidth
                                            variant="filled"
                                            multiline
                                            rows={3}
                                            label="Description"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.description}
                                            name="description"
                                            error={!!touched.description && !!errors.description}
                                            helperText={touched.description && errors.description}
                                            sx={{ gridColumn: 'span 12' }}
                                        />

                                        <FormControlLabel
                                            sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                                            control={
                                                <Checkbox
                                                    checked={values.isLunar}
                                                    onChange={handleChange}
                                                    name="isLunar"
                                                    color="primary"
                                                />
                                            }
                                            label="Is Lunar"
                                        />

                                        <FormControlLabel
                                            sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                                            control={
                                                <Checkbox
                                                    checked={values.isActive}
                                                    onChange={handleChange}
                                                    name="isActive"
                                                    color="primary"
                                                />
                                            }
                                            label="Is Active"
                                        />
                                        
                                        <Button
                                            fullWidth
                                            type="submit"
                                            variant="contained"
                                            color="secondary"
                                            sx={{ gridColumn: "span 12", height: 42 }}
                                        >
                                            {holidayId ? "Update" : "Create"}
                                        </Button>
                                    </Box>
                                </form>
                            )}
                        </Formik>
                    </LocalizationProvider>
                </Box>
            )}
        </Box>
    );
};

export default HolidayPage;
